import { useRef } from 'react';
import {
  convertWeightByMaxRatio,
  fromCanonicalKg,
  isEffortDerivedWeightSource,
  isMaxTrustedForProposal,
  isPrefilledWeightSource,
  resolveDefaultWeight,
  roundLoadable,
  weightedLastSets,
  toCanonicalKg,
  type CurrentMax,
  type EquipmentDefault,
  type Exercise,
  type LastLoggedSet,
  type LoadableContext,
  type LocationUnavailable,
  type WeightSource,
  parseLocaleNumber,
  plateLoadModeFor,
} from '@heraion/shared';

import type { ConfirmFn } from '../seams';
import type { DraftEntry, LoggerDispatch } from './state/loggerDraft';
import { resolveSeededLoadedSides } from './state/bilateralPreference';
import {
  ensureRecentWorkoutsCached,
  getLastLoggedSetForSlug,
  useEntryHistoryApiFetch,
} from './useEntryHistory';
import { lastLoggedSetsBeyondWindow } from './lastLoggedBeyondWindow';
import { getSilentInstanceAt } from './silentInstance';
import { loadPlateLoadShape, useRoundingContextRef } from './state/plateConfig';
import { rememberInstance } from './_mintedInstances';
import {
  catalogLoadedSides,
  deriveEntryKind,
  kindNeedsDuration,
} from './deriveEntryKind';

// Swap an entry's exercise, from either the swap picker or the tip modal's
// "pick an alternative". A cross-kind swap (reps-family ↔ duration-family)
// resets the entry's sets (the reducer blanks them), so if any logged set
// carries data we confirm first — otherwise the reset would silently discard
// it (IB-6). Same-kind swaps and empty entries replace without a prompt.
//
// EQ-1 — the equipment instance is resolved for the exercise being swapped IN,
// never inherited from the one going out. Instance names are per exercise
// (equipment-model.md §8.5, point 4): the leg press's "Machine A" and the pec deck's
// are different machines that happen to share a letter, so carrying the value
// across a swap would mint an identity the athlete never logged. Both callers
// go through here, so the rule is stated once:
//
//   caller passed a pick  → use it (the swap picker asked and got an answer)
//   exactly one known     → apply it silently, same as the add path
//   none or several       → null, and the athlete sets it from the picker
//
// A replace to the SAME exercise keeps its logged sets (the reducer only
// blanks them across kinds), so switching machine mid-exercise re-stamps the
// tag on work already done rather than restarting it (§8.4).
// ⛔ The confirm goes through the SEAM, not through react-native's Alert.
// This hook lives in packages/ui, and `Alert` is a no-op on react-native-web
// (seams.ts states the contract): the cross-kind warning below therefore never
// appeared on web-rnw — the swap just went through and the logged sets were
// cleared with nothing asked. Fixed 2026-08-17 alongside EQ-1-GYM-COLDSTART,
// with Antenore's explicit go-ahead. It is a plain yes/no, so `ConfirmFn` is
// the right shape here (the ✕ dialog's checkbox list is the case that cannot
// use it, and does not).
// EQ-1-SWAP-PCTMAX — what the swapped-in exercise's pending cells should say.
//
// A chain, and the ORDER is the whole design (Antenore, 2026-08-20 + LG-58
// 2026-09-01):
//
//   0. LG-58 — an EFFORT-prescribed set (weightSource in the effort-* family)
//      carries a PRESCRIPTION, not a weight: re-run the SAME chain plan-start
//      uses (resolveDefaultWeight, effort arm) against the NEW exercise's own
//      MAX and history, so the load still answers "how much to land on RIR 2"
//      and its provenance stays effort-* (the proposal cue survives the swap).
//      Blank when the new exercise has no usable data — the target rides on,
//      the cell waits for it. Runs BEFORE the history/ratio steps: for an
//      effort set those would drop the prescription and flip provenance to
//      'history', which is the bug his 1/09 smoke caught.
//   1. history on the NEW exercise → use it. His words: *"per un attrezzo
//      alternativo ha senso usare lo storico"*. It is a load he really moved
//      on this exercise, so it borrows no MAX and inherits no MAX error.
//   2. otherwise convert the outgoing weight through the two MAXes — but only
//      when BOTH are trusted. On his own account 65 of 115 MAXes were
//      `calculated_seed`, and the 2026-05-28 audit-heal shows one running 51%
//      HIGH; a first set on an unfamiliar exercise is the worst place to hand
//      over a number like that.
//   3. otherwise blank, which is exactly today's behaviour.
//
// ⛔ Bodyweight is out of scope on either side: a MAX there is TOTAL load while
// the logger shows the EXTRA, so the ratio would compare two different things.
// ⛔ Only sets whose weight the CHAIN filled are touched. A weight the athlete
// typed survives untouched, as it already did.
const carryWeightForSwap = (params: {
  entry: DraftEntry;
  toSlug: string;
  toIsBodyweight: boolean;
  /** CN-39: la forma di carico dell'esercizio di ARRIVO. Il peso portato si
   *  floora sulla scaletta di QUEL ferro: uno swap verso un landmine non deve
   *  arrotondare come se i dischi andassero da due parti.
   *  CN-45: la scelta salvata dall'atleta quando c'è (passaggio tardivo),
   *  altrimenti il default di catalogo (passaggio sincrono). */
  toBarLoading: "standard" | "single-side" | undefined;
  /** The new exercise's last logged set: the sync window's answer at the
   *  swap, the index's when it lands late. Passed in, not read here, so the
   *  second pass differs from the first only by this. */
  lastLogged: LastLoggedSet | null;
  currentMaxes: Map<string, CurrentMax> | null;
  roundingContext: LoadableContext | undefined;
}): ReadonlyArray<{ weight: string; source: WeightSource } | null> => {
  const {
    entry,
    toSlug,
    toIsBodyweight,
    toBarLoading,
    lastLogged,
    currentMaxes,
    roundingContext,
  } = params;
  const blank = entry.sets.map(() => null);
  if (entry.isBodyweight || toIsBodyweight) return blank;

  // Step 1, resolved once per swap rather than per set: the answer does not
  // depend on which set is asking.
  const historyKg = lastLogged?.weightKg ?? null;

  const fromMax = currentMaxes?.get(entry.exerciseId);
  const toMax = currentMaxes?.get(toSlug);
  const maxesUsable =
    isMaxTrustedForProposal(fromMax?.source) &&
    isMaxTrustedForProposal(toMax?.source);

  return entry.sets.map((s) => {
    if (s.completedAt !== null) return null;
    if (!isPrefilledWeightSource(s.weightSource)) return null;
    const asDisplay = (kg: number, source: WeightSource) => ({
      weight: String(fromCanonicalKg(kg, s.unit)),
      source,
    });
    // Step 0 (LG-58): an effort-prescribed set re-resolves its load from the
    // NEW exercise's own numbers, not by carrying the old one. `reps` is the
    // prescribed count and `rirPrescribed` the canonical target; without both
    // there is no "N + r to failure" and so no load — leave it blank rather
    // than invent one. No `roundLoadable` here: plan-start doesn't floor the
    // effort arm either, so a fresh start and a swap-in land on the same rung.
    if (isEffortDerivedWeightSource(s.weightSource)) {
      const reps = Number(s.reps);
      // Guard the effort intent up front: a finite, non-negative target and a
      // real (>0) rep count. Slightly STRICTER than effortCandidates's own
      // check — a 0-rep set has nothing to work a load back from — so a
      // malformed prescription blanks here, never slips into the generic
      // fallthrough, and never hands back a number for a set nobody could do.
      if (
        s.rirPrescribed === undefined ||
        !Number.isFinite(s.rirPrescribed) ||
        s.rirPrescribed < 0 ||
        !Number.isFinite(reps) ||
        reps <= 0
      ) {
        return null;
      }
      const resolved = resolveDefaultWeight(
        { kind: 'none' },
        {
          userMax: toMax?.valueKg,
          userMaxSource: toMax?.source,
          userMaxSetAt: toMax?.setAt,
          lastLogged: lastLogged ?? undefined,
          effort: { targetRir: s.rirPrescribed, reps },
        },
      );
      // ONLY an effort-* answer carries: it is the one that MIMICS the
      // prescription (worked back to the target from the new exercise's own
      // MAX/e1RM). When the effort arm declines, resolveDefaultWeight falls
      // through to the raw last-logged kg as plain 'history' — a number that
      // never asked "how much for RIR 2" and would arrive here with no cue.
      // For an effort set that is worse than empty, so it blanks instead: no
      // data the effort chain can use ⇒ nothing, the target waits (LG-58).
      return resolved.source !== 'blank' &&
        isEffortDerivedWeightSource(resolved.source)
        ? asDisplay(resolved.kg, resolved.source)
        : null;
    }
    if (historyKg !== null) return asDisplay(historyKg, 'history');
    if (!maxesUsable) return null;
    // ⚠️ `s.weight` is the DISPLAY string, in the set's own unit; the two
    // MAXes are canonical kg. Feeding the raw number in would compare pounds
    // against kilos and skew every ratio by 2.2 for an athlete logging in lb.
    // CN-38 — `parseLocaleNumber`, never `Number`. This is a DISPLAY string,
    // typed on a `decimal-pad` that prints the locale's own separator: on
    // it-IT / fr-FR / de-DE the athlete writes `72,5`, `Number` answers NaN,
    // and the guard below turns that into `undefined` — so the swap landed a
    // BLANK cell with no error anywhere. A comma cost the weight silently.
    const outgoing = parseLocaleNumber(s.weight);
    const converted = convertWeightByMaxRatio({
      fromWeightKg: Number.isFinite(outgoing)
        ? toCanonicalKg(outgoing, s.unit)
        : undefined,
      fromMaxKg: fromMax?.valueKg,
      toMaxKg: toMax?.valueKg,
    });
    if (converted === null) return null;
    // Floor to a rung the athlete can actually build — a ratio lands on 68.38
    // otherwise. Same helper and same reason as the plan-start seed. The full
    // context (both units) rides through so a lb row floors on the athlete's
    // OWN lb inventory rather than the kg one, or the DEFAULT_LOADABLE_STEP_LB
    // fallback; an undefined context falls back to roundLoadable's own
    // per-unit default, which is the un-configured behaviour, not a wrong
    // number.
    const loadable = roundLoadable(
      converted,
      s.unit,
      roundingContext,
      plateLoadModeFor(toBarLoading),
    );
    if (loadable <= 0) return null;
    // 'computed-percent' and not a new provenance value: the number WAS
    // computed as a fraction of MAX, and LogSet.weightSource is a closed
    // persisted enum — a fifth value would make every set carrying it fail
    // LogSet.safeParse on the family's older build, which drops the set
    // wholesale from the history window (useEntryHistory's fetch).
    return asDisplay(loadable, 'computed-percent');
  });
};

export function useReplaceExercise({
  entries,
  dispatch,
  t,
  confirm,
  locationUnavailable,
  currentMaxes,
  equipmentDefault,
}: {
  entries: DraftEntry[];
  dispatch: LoggerDispatch;
  t: (k: string, opts?: Record<string, unknown>) => string;
  confirm: ConfirmFn;
  /** EQ-1-SWAP-PCTMAX — the athlete's stored MAXes, host-resolved, null until
   *  they load. Same shape and same null-while-loading contract as
   *  useSetCyclePill's `currentMaxes`; a null simply means the conversion step
   *  cannot run and the chain falls through to blank, as it does today. */
  currentMaxes?: Map<string, CurrentMax> | null;
  /** EQ-1-PIN-DEFAULT — the athlete's elected machine per (place, exercise).
   *  Same host-profile door as `locationUnavailable`, and read with the same
   *  place, so an election and a demotion can never be about different rooms. */
  equipmentDefault?: EquipmentDefault;
  /** EQ-1-GYM-COLDSTART R6 — the athlete's declared absences, off the host
   *  profile, so the silent branch below cannot attach a machine declared not
   *  to be at this entry's place. */
  locationUnavailable?: LocationUnavailable;
}): (
  entryIdx: number,
  ex: Exercise,
  close: () => void,
  /** The swap picker's answer when it asked. Omitted = nobody asked. */
  pickedInstance?: string | null,
) => void {
  // Live view of `entries` for async closures (the sticky-laterality overlay
  // below resolves after AsyncStorage; the render-time array would be stale
  // by then).
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  // The athlete's finest plate rung in both units, for flooring a converted
  // weight on whichever unit the swapped-in row uses.
  const roundingContextRef = useRoundingContextRef();
  // Null outside the Provider: the late index pass then simply does not run.
  const apiFetch = useEntryHistoryApiFetch();

  return (
    entryIdx: number,
    ex: Exercise,
    close: () => void,
    pickedInstance?: string | null,
  ): void => {
    close();
    const flags = deriveEntryKind(ex);
    const entry = entries[entryIdx];
    const crosses = entry
      ? kindNeedsDuration(entry) !== kindNeedsDuration(flags)
      : false;
    // Stricter than hasCommittedWork (loggerDraftPersistence) on purpose: this
    // only decides whether to WARN before the reducer's unconditional
    // cross-kind reset, so a completed-but-empty set needn't trigger a scary
    // "your data will be cleared" prompt — there's nothing to lose.
    const hasLoggedData =
      entry?.sets.some(
        (s) =>
          s.completedAt !== null &&
          (s.reps.trim() !== '' ||
            s.weight.trim() !== '' ||
            s.duration.trim() !== '' ||
            s.distanceM.trim() !== ''),
      ) ?? false;
    const doReplace = () => {
      // IB-8: seed the NEW exercise's laterality with the swap (catalog
      // default sync, sticky per-slug pick async) — mirrors useAddExercise.
      // Before this the swap kept the old exercise's loadedSides, so a
      // bench-press → dumbbell-chest-press swap counted the two-dumbbell
      // work ×1 (inbox 5aa7017d).
      const catalogDefault = catalogLoadedSides(ex);
      // R6 — the silent branch narrows by what was DECLARED absent at this
      // ENTRY's place (the same key the picker ranks and writes by, and more
      // truthful than the session's when the athlete moved mid-workout).
      // ⛔ Only the silent branch: an explicit pick is the athlete answering,
      // and an answer is never second-guessed — including the explicit `null`.
      const resolved =
        pickedInstance !== undefined
          ? pickedInstance
          : // EQ-1-LASTHERE / PIN-DEFAULT: the ENTRY's place drives the
            // absence lookup, the last-here gate and the pin alike.
            getSilentInstanceAt(
              ex.slug,
              entry?.location ?? null,
              locationUnavailable,
              equipmentDefault,
            );
      // Remember any machine the athlete named out loud, so one coined and
      // then replaced in the same unsaved session does not evaporate — saved
      // history is the only other place a name lives, and it has not seen
      // this workout yet.
      if (resolved) rememberInstance(ex.slug, resolved);
      // EQ-1-SWAP-PCTMAX. Computed HERE and passed as data: the reducer is
      // pure over the draft and has no maxes, no history cache and no plate
      // config — the same division of labour `equipmentInstance` already
      // follows on this action. Only for a genuine change of exercise: a
      // same-exercise re-stamp (naming a machine) keeps its cells untouched,
      // so there is nothing to carry.
      const syncLast = getLastLoggedSetForSlug(ex.slug);
      const carryFor = (
        lastLogged: LastLoggedSet | null,
        barLoading: Exercise['barLoading'],
      ) =>
        entry && !crosses && entry.exerciseId !== ex.slug
          ? carryWeightForSwap({
              entry,
              toSlug: ex.slug,
              toIsBodyweight: ex.isBodyweight === true,
              toBarLoading: barLoading,
              lastLogged,
              currentMaxes: currentMaxes ?? null,
              roundingContext: roundingContextRef.current,
            })
          : undefined;
      const carriedWeights = carryFor(syncLast, ex.barLoading);
      dispatch({
        type: 'replace_exercise',
        entryIdx,
        exerciseId: ex.slug,
        exerciseName: ex.name.en,
        ...flags,
        loadedSides: catalogDefault,
        equipmentInstance: resolved,
        ...(carriedWeights ? { carriedWeights } : {}),
      });
      // The swap above landed with the catalog shape and the window's last
      // set. The shape the athlete saved, and the index when the window is
      // blind (a lift resumed after a break), arrive late: they only refresh
      // cells still exactly as the swap left them.
      if (entry && carriedWeights) {
        void (async () => {
          const [stored, found] = await Promise.all([
            loadPlateLoadShape(ex.slug),
            syncLast === null && apiFetch
              ? ensureRecentWorkoutsCached(apiFetch).then(
                  async (window) =>
                    weightedLastSets(
                      await lastLoggedSetsBeyondWindow([ex.slug], window, apiFetch),
                    ).get(ex.slug) ?? null,
                )
              : Promise.resolve(syncLast),
          ]);
          const refreshed = carryFor(found, stored ?? ex.barLoading);
          if (
            !refreshed ||
            refreshed.every(
              (r, i) =>
                r?.weight === carriedWeights[i]?.weight &&
                r?.source === carriedWeights[i]?.source,
            )
          ) {
            return;
          }
          dispatch({
            type: 'refresh_carried_weights',
            entryIdx,
            exerciseId: ex.slug,
            expected: carriedWeights,
            carriedWeights: refreshed,
          });
        })();
      }
      if (ex.movementPattern !== 'cardio') {
        void resolveSeededLoadedSides(ex.slug, catalogDefault).then((sides) => {
          if (sides === catalogDefault) return;
          // Re-check the entry still holds the swapped exercise — the async
          // gap could race a second swap or a remove.
          const current = entriesRef.current[entryIdx];
          if (!current || current.exerciseId !== ex.slug) return;
          dispatch({
            type: 'set_entry_loaded_sides',
            entryIdx,
            loadedSides: sides,
          });
        });
      }
    };
    if (crosses && hasLoggedData) {
      // Fire-and-forget, same as the Alert it replaces: the caller has already
      // closed the picker and has nothing to await. A dismissed dialog
      // resolves false (ConfirmFn's contract), so tapping out never clears a
      // set.
      void confirm({
        title: t('mobile.logger.swapKindTitle', {
          defaultValue: 'Change exercise type?',
        }),
        body: t('mobile.logger.swapKindBody', {
          defaultValue:
            'This exercise is logged a different way, so the sets you already logged here will be cleared.',
        }),
        confirmLabel: t('mobile.logger.swapKindConfirm', {
          defaultValue: 'Change & clear',
        }),
        cancelLabel: t('mobile.logger.cancel', { defaultValue: 'Cancel' }),
      }).then((ok) => {
        if (ok) doReplace();
      });
    } else {
      doReplace();
    }
  };
}
